/**
 * Inscription à la liste d'attente bêta.
 *
 * Appelle l'edge function `beta-signup`, qui enregistre le lead dans
 * `beta_signups` et déclenche les emails (notification interne + accusé de
 * réception) via Brevo. Remplace l'ancien formulaire Tally externe.
 *
 * L'inscription se fait en deux appels :
 *   1. `submitBetaSignup` — l'email seul, envoyé dès la première validation.
 *      Le lead est acquis à ce moment précis.
 *   2. `submitBetaDetails` — la fiche de qualification, complétée ensuite.
 *
 * Découper ainsi n'est pas cosmétique : si le visiteur abandonne en cours de
 * route, l'email est déjà en base et le prospect reste joignable.
 */

export type BetaSignupSource = 'landing' | 'login' | 'signup' | 'onboarding' | 'pricing';

/** Doit rester aligné sur VALID_JOB_ROLES côté edge function et sur le CHECK en base. */
export type BetaJobRole = 'direction' | 'staffing' | 'recrutement' | 'commerce' | 'autre';

export const JOB_ROLE_OPTIONS: { value: BetaJobRole; label: string }[] = [
  { value: 'direction', label: 'Direction / associé' },
  { value: 'staffing', label: 'Staffing / delivery' },
  { value: 'recrutement', label: 'Recrutement' },
  { value: 'commerce', label: 'Commerce / business development' },
  { value: 'autre', label: 'Autre' },
];

export type BetaSignupDetails = {
  firstName: string;
  lastName: string;
  company: string;
  jobRole: BetaJobRole;
  phone?: string;
  message?: string;
};

/**
 * Discriminant textuel volontaire : le projet compile sans `strictNullChecks`,
 * mode dans lequel TypeScript ne narrow pas une union sur un booléen littéral.
 */
export type BetaSignupResult =
  | { status: 'ok'; duplicate: boolean }
  | { status: 'error'; message: string };

const ENDPOINT =
  import.meta.env.VITE_EDGE_BETA_SIGNUP ?? import.meta.env.VITE_EDGE_BETA_SIGNUP_REC ?? '';

export function isValidEmail(email: string) {
  const value = email.trim();
  const atIndex = value.indexOf('@');
  const domain = atIndex >= 0 ? value.slice(atIndex + 1) : '';
  return value.length > 0 && atIndex > 0 && domain.includes('.') && domain.indexOf('.') < domain.length - 1;
}

async function post(payload: Record<string, unknown>): Promise<BetaSignupResult> {
  if (!ENDPOINT) {
    console.error('VITE_EDGE_BETA_SIGNUP / _REC is not defined in .env');
    return { status: 'error', message: 'Le formulaire est momentanément indisponible. Réessayez plus tard.' };
  }

  try {
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Visiteur non authentifié : la clé anon suffit à passer la gateway.
        'Authorization': `Bearer ${anonKey}`,
        'apikey': anonKey,
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      return { status: 'error', message: data?.error ?? "L'inscription a échoué. Réessayez dans un instant." };
    }

    return { status: 'ok', duplicate: Boolean(data?.duplicate) };
  } catch {
    return { status: 'error', message: 'Connexion impossible. Vérifiez votre réseau et réessayez.' };
  }
}

/** Étape 1 — l'email seul. C'est l'appel qui rend le lead récupérable. */
export function submitBetaSignup(
  email: string,
  source: BetaSignupSource = 'landing',
  company?: string
): Promise<BetaSignupResult> {
  return post({ email: email.trim(), source, company, stage: 'lead' });
}

/** Étape 2 — la fiche de qualification, rattachée au lead par son email. */
export function submitBetaDetails(
  email: string,
  details: BetaSignupDetails,
  source: BetaSignupSource = 'landing'
): Promise<BetaSignupResult> {
  return post({
    email: email.trim(),
    source,
    stage: 'details',
    firstName: details.firstName.trim(),
    lastName: details.lastName.trim(),
    company: details.company.trim(),
    jobRole: details.jobRole,
    phone: details.phone?.trim() || undefined,
    message: details.message?.trim() || undefined,
  });
}
